import { JSX, useCallback } from 'react'
import { LinkOptions, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import classNames from 'classnames'

import { Button } from '@/components/new/ui'
import { useNavigate } from '@/routes'

type PaginatorVariant = 'header' | 'footer'

type PaginatorProps = {
    className?: string
    small?: boolean
    variant: PaginatorVariant
    to: LinkOptions['to']
    params?: LinkOptions['params']
    showMaxPages?: number
    pageSize: number
    count: number
    renderCounter?: (fromCount: number, toCount: number, page: number, count: number) => JSX.Element
}

const btnDisabledClassName = 'text-light pointer-events-none cursor-default'

type PaginationItem = number | 'ellipsis-start' | 'ellipsis-end'

const getVisiblePages = ({
    currentPage,
    pageCount,
    boundaryCount,
}: {
    currentPage: number
    pageCount: number
    boundaryCount: number
}): PaginationItem[] => {
    if (pageCount <= boundaryCount * 2 + 1) {
        return Array.from({ length: pageCount }, (_, index) => index + 1)
    }

    const siblingCount = 1
    const range = (from: number, to: number) => Array.from({ length: to - from + 1 }, (_, index) => from + index)

    if (currentPage <= boundaryCount) {
        const endPage = Math.max(boundaryCount, currentPage + siblingCount)

        if (endPage >= pageCount - 1) {
            return range(1, pageCount)
        }

        return [...range(1, endPage), 'ellipsis-end', pageCount]
    }

    if (currentPage > pageCount - boundaryCount) {
        const startPage = Math.min(pageCount - boundaryCount + 1, currentPage - siblingCount)

        if (startPage <= 2) {
            return range(1, pageCount)
        }

        return [1, 'ellipsis-start', ...range(startPage, pageCount)]
    }

    const leftSibling = currentPage - siblingCount
    const rightSibling = currentPage + siblingCount

    return [1, 'ellipsis-start', ...range(leftSibling, rightSibling), 'ellipsis-end', pageCount]
}

export const Paginator = ({
    className,
    small,
    variant,
    to,
    params,
    showMaxPages = 3,
    pageSize,
    count,
    renderCounter,
}: PaginatorProps) => {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const { page: pageSource, ...otherSearch } = useSearch({
        strict: false,
    })

    const createSearch = useCallback(
        (page: number) => ({
            ...otherSearch,
            page,
        }),
        [otherSearch]
    )

    const parsedPage =
        typeof pageSource === 'number'
            ? pageSource
            : typeof pageSource === 'string'
              ? Number.parseInt(pageSource, 10)
              : 1
    const currentPage = Number.isFinite(parsedPage) && parsedPage > 0 ? Math.floor(parsedPage) : 1

    const btnClassName = classNames(
        'relative font-medium flex items-center justify-center leading-none m-0.5 hover:bg-neutral-100 active:bg-white rounded-full dark:hover:bg-neutral-800 dark:active:bg-neutral-900',
        {
            'size-8': !small,
            'size-6': small,
        }
    )

    const renderButton = useCallback(
        (page: number) => {
            const isCurrentPage = page === currentPage

            return (
                <Button
                    key={page}
                    type="button"
                    variant="ghost"
                    size={small ? 'icon-xs' : 'icon-sm'}
                    className={classNames(btnClassName, {
                        'bg-white shadow-sm disabled:opacity-100 dark:bg-neutral-900': isCurrentPage,
                    })}
                    disabled={isCurrentPage}
                    aria-current={isCurrentPage ? 'page' : undefined}
                    onClick={() => void navigate({ to, params, search: createSearch(page) })}
                >
                    {page}
                </Button>
            )
        },
        [to, params, createSearch, btnClassName, currentPage, navigate, small]
    )

    const pageCount = count > 0 ? Math.ceil(count / pageSize) : 0
    const fromCount = count > 0 ? (currentPage - 1) * pageSize + 1 : 0
    const toCount = Math.min(currentPage * pageSize, count)

    const visiblePages = getVisiblePages({
        currentPage,
        pageCount,
        boundaryCount: showMaxPages,
    })

    const pages = visiblePages.map((item) => {
        if (typeof item === 'number') {
            return renderButton(item)
        }

        return (
            <span key={item} className={classNames(btnClassName, btnDisabledClassName, 'px-4')}>
                ...
            </span>
        )
    })

    const counter = renderCounter ? (
        renderCounter(fromCount, toCount, currentPage, count)
    ) : (
        <div
            className={classNames({
                'text-base font-medium': variant === 'header',
                'font-medium': variant === 'footer',
            })}
        >
            <span>
                <span>
                    {t('components.pagination.show', {
                        fromCount,
                        toCount,
                    })}
                </span>
                <span>&nbsp;</span>
                <span className="text-muted-foreground">
                    {t('components.pagination.total', {
                        count,
                    })}
                </span>
            </span>
        </div>
    )

    return (
        <div
            className={classNames(className, 'flex items-center', {
                'text-xs': small,
                'justify-between': variant === 'footer',
                'gap-x-4': variant === 'header',
            })}
        >
            {variant === 'footer' && <div className="hidden md:block">{counter}</div>}
            {pageCount > 1 && (
                <nav
                    className="flex flex-1 justify-between rounded-full bg-neutral-200 md:flex-initial dark:bg-neutral-700"
                    aria-label={t('components.pagination.label')}
                >
                    <Button
                        type="button"
                        variant="ghost"
                        size={small ? 'icon-xs' : 'icon-sm'}
                        className={classNames(btnClassName, {
                            [btnDisabledClassName]: currentPage <= 1,
                        })}
                        disabled={currentPage <= 1}
                        onClick={() =>
                            void navigate({ to, params, search: createSearch(currentPage - 1) })
                        }
                    >
                        <span className="sr-only">{t('components.pagination.previousPage')}</span>
                        <ChevronLeftIcon
                            className={classNames({
                                'h-4 w-4': small,
                                'h-5 w-5': !small,
                            })}
                            aria-hidden="true"
                        />
                    </Button>
                    <div className="flex shrink-0">{pages}</div>
                    <Button
                        type="button"
                        variant="ghost"
                        size={small ? 'icon-xs' : 'icon-sm'}
                        className={classNames(btnClassName, {
                            [btnDisabledClassName]: currentPage >= pageCount,
                        })}
                        disabled={currentPage >= pageCount}
                        onClick={() =>
                            void navigate({ to, params, search: createSearch(currentPage + 1) })
                        }
                    >
                        <span className="sr-only">{t('components.pagination.nextPage')}</span>
                        <ChevronRightIcon
                            className={classNames({
                                'h-4 w-4': small,
                                'h-5 w-5': !small,
                            })}
                            aria-hidden="true"
                        />
                    </Button>
                </nav>
            )}
            {variant === 'header' && <div className="hidden md:block">{counter}</div>}
        </div>
    )
}
